import { useQuery } from '@tanstack/react-query';
// api
import { dashboardApi } from '@/domains/traffic-monitoring/apis/dashboard';
// constant
import { DASHBOARD_CONSTANTS } from '@/domains/traffic-monitoring/constants/dashboard.constants';
import type {
  TrafficVolumeSpeedTrendBucket,
  TrafficVolumeSpeedTrendParams,
} from '../types/dashboard.types';

export interface TrafficTrendPoint {
  label: string; // "HH:00" WIB
  lineValue: number; // volume (top line/area)
  barValue: number; // avg speed km/h (bottom bars)
}

// bucket_start is ISO with the +07:00 offset already applied server-side, so the
// hour can be read straight off the string (chars 11-16 → "HH:mm").
const toPoint = (b: TrafficVolumeSpeedTrendBucket): TrafficTrendPoint => ({
  label: b.bucket_start.slice(11, 16),
  lineValue: b.volume,
  barValue: b.avg_speed_kph ?? 0,
});

// PSC_PC0203001_BE_005 - last 24h of area-wide volume + volume-weighted avg
// speed, in 1h buckets. Feeds the "Volume Trends & Avg.Speed" BarLineChart.
export const useTrafficVolumeTrend = (params: TrafficVolumeSpeedTrendParams = {}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['trafficVolumeSpeedTrend', params],
    queryFn: () => dashboardApi.getTrafficVolumeSpeedTrend(params),
    staleTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.STALE_TIME_MS,
    gcTime: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.CACHE_TIME_MS,
    refetchInterval: DASHBOARD_CONSTANTS.CROSSROAD_DASHBOARD.REFETCH_INTERVAL_MS,
    placeholderData: (prev) => prev,
  });

  return {
    trend: (data?.data?.trend ?? []).map(toPoint),
    isLoading,
    isError,
  };
};
